import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiCall } from '@/App';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, ArrowRight, ChevronRight, Zap, Shield, Clock, Star } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import MobileMenu from '@/components/MobileMenu';

function generateFAQs(catName, stats, platform) {
  const name = catName.toLowerCase();
  return [
    { q: `Berapa harga ${catName} di Flexa?`, a: `Harga ${catName} bermula dari serendah RM${stats.min_price.toFixed(2)} untuk 1,000 unit. Harga purata adalah RM${stats.avg_price.toFixed(2)}/1K. Flexa menawarkan harga paling murah di Malaysia untuk servis ini.` },
    { q: `Selamat ke beli ${catName}?`, a: `100% selamat. Flexa tidak memerlukan password akaun anda. Semua delivery dilakukan melalui API rasmi. Data anda disulitkan dan privacy terjamin sepenuhnya.` },
    { q: `Berapa lama delivery ${catName}?`, a: `Purata delivery bermula dalam masa 60 saat hingga beberapa jam bergantung jenis servis yang dipilih. Kebanyakan order bermula dalam masa kurang 5 minit.` },
    { q: `Ada guarantee untuk ${catName}?`, a: `Sebahagian servis datang dengan guarantee refill. Jika berlaku drop, anda boleh request refill secara automatik. Servis tanpa guarantee biasanya lebih murah.` },
  ];
}

const BRAND = { tiktok: 'TikTok', youtube: 'YouTube', linkedin: 'LinkedIn', soundcloud: 'SoundCloud', whatsapp: 'WhatsApp', twitterx: 'Twitter/X', my: 'MY', ig: 'IG', tt: 'TT', yt: 'YT', seo: 'SEO', andamp: '&', and: '&' };
// Nama sementara dari slug supaya H1/meta dipapar serta-merta (diganti nama sebenar kategori bila API tiba)
const nameFromSlug = slug => slug.split('-').map(w => BRAND[w] || w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

export default function BeliServicePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiCall('get', `/seo/beli/${slug}`)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (!loading && !data) return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
      <Helmet><title>Halaman tidak dijumpai | Flexa</title><meta name="robots" content="noindex, follow" /></Helmet>
      <p className="text-lg font-bold text-zinc-900 mb-2">Halaman tidak dijumpai</p>
      <Link to="/smm-panel-malaysia" className="text-violet-600 hover:underline">Kembali ke SMM Panel Malaysia</Link>
    </div>
  );

  const name = data ? data.category.name : nameFromSlug(slug);
  const { services = [], total_services, stats, platform, related_categories = [] } = data || {};
  const url = `https://flexaviral.com/beli/${slug}`;
  const priceTxt = stats ? `Dari RM${stats.min_price.toFixed(2)}/1K` : 'Harga Termurah';
  const desc = data
    ? `Beli ${name} dengan harga termurah di Malaysia. ${total_services} servis dari RM${stats.min_price.toFixed(2)}/1K. Auto-delivery 24/7, 100% selamat. Daftar percuma di Flexa.`
    : `Beli ${name} dengan harga termurah di Malaysia. Auto-delivery 24/7, 100% selamat tanpa password. Daftar percuma di Flexa, Panel SMM #1 Malaysia.`;
  const faqs = data ? generateFAQs(name, stats, platform) : [];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{`Beli ${name} Malaysia | ${priceTxt} - Flexa`}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`Beli ${name} Malaysia | ${priceTxt} - Flexa`} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={url} />
        {data && <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": `${name} Malaysia`,
          "description": `Beli ${name} termurah di Malaysia dengan auto-delivery 24/7.`,
          "image": "https://flexaviral.com/og-image.jpg",
          "brand": {"@type": "Brand", "name": "Flexa"},
          "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": "MYR",
            "lowPrice": stats.min_price.toFixed(2),
            "highPrice": stats.max_price.toFixed(2),
            "offerCount": total_services,
            "availability": "https://schema.org/InStock"
          }
        })}</script>}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Flexa", "item": "https://flexaviral.com"},
            {"@type": "ListItem", "position": 2, "name": "SMM Panel Malaysia", "item": "https://flexaviral.com/smm-panel-malaysia"},
            {"@type": "ListItem", "position": 3, "name": name, "item": url}
          ]
        })}</script>
        {data && <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": {"@type": "Answer", "text": f.a}
          }))
        })}</script>}
      </Helmet>
      {/* Nav */}
      <nav className="bg-white border-b border-zinc-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            <img src="/flexa-wordmark.png" alt="Flexa" className="h-6" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/smm-panel-malaysia" className="text-sm text-zinc-500 hover:text-zinc-900 hidden sm:inline">SMM Panel</Link>
            <Link to="/services" className="text-sm text-zinc-500 hover:text-zinc-900 hidden sm:inline">Semua Servis</Link>
            <Link to="/signup" className="h-9 px-4 rounded-full bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 flex items-center gap-1.5">
              Daftar <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <MobileMenu className="sm:hidden" />
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center gap-1.5 text-xs text-zinc-400">
          <Link to="/" className="hover:text-violet-600">Flexa</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/smm-panel-malaysia" className="hover:text-violet-600">SMM Panel Malaysia</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-zinc-700 font-medium">{name}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-b from-violet-50 to-white py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-zinc-900 tracking-tight mb-4">
            Beli {name} Malaysia
          </h1>
          <p className="text-base text-zinc-600 max-w-2xl mx-auto mb-6">
            {data ? <>{total_services} servis {name} dengan harga bermula <strong>RM{stats.min_price.toFixed(2)}/1K</strong>. </> : <>Servis {name} dengan harga termurah. </>}
            Auto-delivery 24/7, 100% selamat tanpa password. Panel SMM termurah di Malaysia.
          </p>
          <div className="flex items-center justify-center gap-4 text-sm text-zinc-500 mb-8">
            <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-emerald-500" /> {data ? `${total_services} servis` : 'Servis dipilih'}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-blue-500" /> Delivery 60s</span>
            <span className="flex items-center gap-1.5"><Shield className="h-4 w-4 text-violet-500" /> 100% selamat</span>
          </div>
          <Link to="/signup" className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-violet-600 text-white font-bold hover:bg-violet-700">
            Order Sekarang <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {!data && (
        <div className="py-16 flex items-center justify-center" data-testid="beli-loading">
          <div className="h-8 w-8 border-3 border-violet-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {data && <>
      {/* Price Stats */}
      <section className="py-8 border-y border-zinc-100">
        <div className="max-w-4xl mx-auto px-4 grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-extrabold text-violet-600">RM{stats.min_price.toFixed(2)}</p>
            <p className="text-xs text-zinc-500 mt-1">Harga terendah/1K</p>
          </div>
          <div>
            <p className="text-2xl font-extrabold text-violet-600">RM{stats.avg_price.toFixed(2)}</p>
            <p className="text-xs text-zinc-500 mt-1">Harga purata/1K</p>
          </div>
          <div>
            <p className="text-2xl font-extrabold text-violet-600">{total_services}</p>
            <p className="text-xs text-zinc-500 mt-1">Servis tersedia</p>
          </div>
        </div>
      </section>

      {/* Service List */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-6">Senarai Harga {name}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left py-3 px-3 text-xs uppercase text-zinc-500 font-medium">Servis</th>
                  <th className="text-right py-3 px-3 text-xs uppercase text-zinc-500 font-medium">Harga/1K</th>
                  <th className="text-right py-3 px-3 text-xs uppercase text-zinc-500 font-medium hidden sm:table-cell">Min</th>
                  <th className="text-right py-3 px-3 text-xs uppercase text-zinc-500 font-medium hidden sm:table-cell">Max</th>
                  <th className="text-center py-3 px-3 text-xs uppercase text-zinc-500 font-medium hidden md:table-cell">Guarantee</th>
                </tr>
              </thead>
              <tbody>
                {services.slice(0, 30).map((svc, i) => (
                  <tr key={svc.id} className={`border-b border-zinc-50 hover:bg-violet-50/30 ${i === 0 ? 'bg-emerald-50/30' : ''}`}>
                    <td className="py-3 px-3">
                      <p className="font-medium text-zinc-900 text-sm">{svc.name}</p>
                      {i === 0 && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">Paling Murah</span>}
                    </td>
                    <td className="py-3 px-3 text-right font-bold text-violet-600 tabular-nums">RM {svc.price_per_1000.toFixed(2)}</td>
                    <td className="py-3 px-3 text-right text-zinc-500 tabular-nums hidden sm:table-cell">{svc.min_order.toLocaleString()}</td>
                    <td className="py-3 px-3 text-right text-zinc-500 tabular-nums hidden sm:table-cell">{svc.max_order.toLocaleString()}</td>
                    <td className="py-3 px-3 text-center hidden md:table-cell">
                      {svc.guarantee && svc.guarantee !== 'No Guarantee' ? (
                        <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">{svc.guarantee}</span>
                      ) : (
                        <span className="text-[10px] text-zinc-300">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total_services > 30 && (
            <p className="text-center text-sm text-zinc-500 mt-4">
              + {total_services - 30} servis lagi. <Link to="/signup" className="text-violet-600 font-semibold hover:underline">Daftar untuk lihat semua</Link>
            </p>
          )}
        </div>
      </section>

      {/* Why Flexa */}
      <section className="py-12 px-4 bg-zinc-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-6 text-center">Kenapa Beli {name} di Flexa?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: Star, title: 'Harga Termurah', desc: `Bermula dari RM${stats.min_price.toFixed(2)}/1K, 10% lebih murah dari panel lain.` },
              { icon: Zap, title: 'Auto-Delivery', desc: 'Order masuk, terus process. Delivery bermula dalam masa 60 saat.' },
              { icon: Shield, title: 'Selamat 100%', desc: 'Takde password diperlukan. Privacy terjamin sepenuhnya.' },
              { icon: Clock, title: 'Support Malaysia', desc: 'Bayar guna FPX, GrabPay, Touch n Go. Support dalam Bahasa Melayu.' },
            ].map((f, i) => (
              <div key={i} className="flex gap-3 p-4 bg-white rounded-xl border border-zinc-200">
                <div className="h-9 w-9 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                  <f.icon className="h-4 w-4 text-violet-600" />
                </div>
                <div>
                  <h3 className="font-bold text-zinc-900 text-sm">{f.title}</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl font-bold text-zinc-900 mb-6 text-center">Soalan Lazim - {name}</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-zinc-200 rounded-xl px-5">
                <AccordionTrigger className="text-sm font-semibold text-zinc-900 hover:no-underline py-4">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-zinc-600 leading-relaxed pb-4">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Related Categories */}
      {related_categories.length > 0 && (
        <section className="py-12 px-4 bg-zinc-50 border-t border-zinc-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-lg font-bold text-zinc-900 mb-4">Servis {platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : ''} Lain</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {related_categories.map(r => (
                <Link key={r.slug} to={`/beli/${r.slug}`} className="flex items-center gap-2 p-3 bg-white rounded-lg border border-zinc-200 hover:border-violet-300 text-sm font-medium text-zinc-700 hover:text-violet-700 transition-colors">
                  <ChevronRight className="h-3.5 w-3.5 text-violet-400 flex-shrink-0" /> {r.name} ({r.count})
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      </>}

      {/* Internal Links */}
      <section className="py-8 px-4 border-t border-zinc-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-sm font-bold text-zinc-900 mb-3">Pautan Berkaitan</h2>
          <div className="flex flex-wrap gap-2">
            <Link to="/smm-panel-malaysia" className="text-xs text-violet-600 hover:underline bg-violet-50 px-3 py-1.5 rounded-full">SMM Panel Malaysia</Link>
            <Link to="/blog/apa-itu-smm-panel" className="text-xs text-violet-600 hover:underline bg-violet-50 px-3 py-1.5 rounded-full">Apa Itu SMM Panel?</Link>
            <Link to="/blog/cara-naikkan-followers-instagram" className="text-xs text-violet-600 hover:underline bg-violet-50 px-3 py-1.5 rounded-full">Cara Naikkan Followers</Link>
            <Link to="/services" className="text-xs text-violet-600 hover:underline bg-violet-50 px-3 py-1.5 rounded-full">Semua Servis</Link>
            <Link to="/blog/beli-followers-instagram-selamat-ke" className="text-xs text-violet-600 hover:underline bg-violet-50 px-3 py-1.5 rounded-full">Beli Followers Selamat Ke?</Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-4 bg-violet-600 text-white text-center">
        <h2 className="text-xl sm:text-2xl font-bold mb-3">Sedia Untuk Order {name}?</h2>
        <p className="text-violet-200 mb-6">Daftar percuma. {stats ? `Harga dari RM${stats.min_price.toFixed(2)}/1K.` : 'Harga termurah di Malaysia.'}</p>
        <Link to="/signup" className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-white text-violet-700 font-bold hover:bg-violet-50">
          Daftar Percuma <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 bg-zinc-900 text-center">
        <p className="text-xs text-zinc-500">&copy; {new Date().getFullYear()} Flexa (flexaviral.com)</p>
        <p className="text-[10px] text-zinc-600 mt-1 tracking-wider uppercase">Powered by <span className="text-zinc-400 font-semibold">UNDERADS</span></p>
      </footer>
    </div>
  );
}
